import React, { Context, createContext, FC, ReactElement, ReactNode, useState } from 'react'
import { ReactState, ReactStatePromise } from '../Types/Types'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { useStateWithPromise } from '../Hooks/useStateWithPromise'

type Props = {
    children: ReactNode
}

export type FormContextType = {
    defaultedFields: Record<string, string | boolean>
    isTouched?: boolean
    dirtyFields: Record<string, string | boolean>
    handleFormChange?: (name?: string) => void
    resetDirtyFields?: () => void
    resetDirtyFieldsByNamespace: (namespace: string) => Promise<unknown>
    resetDirtyField: (name: string) => void
    handleDefaultedField?: (name: string) => void
    resetDefaultedField: (name: string) => void
}

export const CommonFormContextProvider: FC<Props> = ({ children }: Props): ReactElement => {
    const [isTouched, setIsTouched]: ReactState<boolean> = useState<boolean>(false)
    const [dirtyFields, setDirtyFields]: ReactStatePromise<Record<string, string | boolean>> = useStateWithPromise<
        Record<string, string | boolean>
    >({})
    const [defaultedFields, setDefaultedFields]: ReactState<Record<string, string | boolean>> = useState<
        Record<string, string | boolean>
    >({})

    const handleFormChange = (name?: string): void => {
        if (name) {
            setDirtyFields(
                (prev: Record<string, string | boolean>): Record<string, string | boolean> => ({
                    ...prev,
                    ...{ [name]: true }
                })
            )
        }

        setIsTouched(true)
    }

    const resetDirtyFields = (): void => {
        setDirtyFields({})
    }
    const resetDirtyFieldsByNamespace = (namespace: string): Promise<unknown> =>
        setDirtyFields((prev: Record<string, string | boolean>): Record<string, string | boolean> => {
            const newDirtyFields: Record<string, string | boolean> = {}

            Object.keys(prev).forEach((key: string): void => {
                if (!key.startsWith(namespace)) {
                    newDirtyFields[key] = prev[key]
                }
            })

            return newDirtyFields
        })
    const resetDirtyField = (name: string): void => {
        setDirtyFields(
            (prev: Record<string, string | boolean>): Record<string, string | boolean> => ({
                ...prev,
                [name]: typeof prev[name] === 'string' ? EMPTY_STRING : false
            })
        )
    }

    const handleDefaultedField = (name: string): void => {
        setDefaultedFields(
            (prev: Record<string, string | boolean>): Record<string, string | boolean> => ({
                ...prev,
                ...{ [name]: true }
            })
        )
    }
    const resetDefaultedField = (name: string): void =>
        setDefaultedFields(
            (prev: Record<string, string | boolean>): Record<string, string | boolean> => ({
                ...prev,
                [name]: typeof prev[name] === 'string' ? EMPTY_STRING : false
            })
        )

    return (
        <CommonFormContext.Provider
            value={{
                defaultedFields,
                isTouched,
                dirtyFields,
                handleFormChange,
                resetDirtyFields,
                resetDirtyFieldsByNamespace,
                resetDirtyField: (name: string): void => resetDirtyField(name),
                handleDefaultedField,
                resetDefaultedField: (name: string): void => resetDefaultedField(name)
            }}
        >
            {children}
        </CommonFormContext.Provider>
    )
}

export const CommonFormContext: Context<Nullable<FormContextType>> = createContext<Nullable<FormContextType>>(undefined)
